/**
 * Conexión con Snov (`/activa/emails/conexion`, sólo el equipo): la última actualización, las casillas de la cuenta
 * con su calentamiento, y de qué comercio es cada campaña.
 *
 * Cómo se decide el comercio de una campaña (backend `comercioDeCampana`):
 * - lo normal es por la CASILLA que la manda: se le elige el comercio a la casilla una vez y todas sus campañas (las
 *   de hoy y las que vengan) quedan de ese comercio;
 * - a mano, para una campaña puntual, gana sobre la casilla;
 * - «No es de un comercio» (una campaña de Deenex o una prueba): no la ve nadie de un cliente.
 * En la duda (casilla sin comercio, o casillas de dos comercios) la campaña no es de nadie: mostrarle a un cliente la
 * campaña y los mails de las personas de otro es lo que no puede pasar. La sugerencia por el nombre no se aplica sola.
 *
 * Elegir en un select no asigna nada (con el foco en un select, una flecha ya lo cambia): se ve qué va a pasar y se
 * confirma con «Guardar». Si la casilla ya era de otro comercio, lo que ya mandó se queda con ése, salvo que el equipo
 * diga que pasa (porque el de antes estaba mal).
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { emailsApi } from "../api";
import type { CampanaConexion, ComercioCorto, Conexion, ModoAsignacion, RemitenteConexion, ResultadoAsignacion } from "../types";
import { usePedido } from "../hooks/usePedido";
import { useEstadoEmails } from "../contexto";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import { Tarjeta } from "../../wa-oficial/components/Tarjeta";
import { Esqueleto, ErrorTarjeta, Vacio } from "../../wa-oficial/components/estadisticas/comun";
import { haceTexto } from "../../wa-oficial/logic/estadisticas";
import { num, plural } from "../../campana-nueva/utils/format";
import { ChipEstadoCampana } from "../components/Chips";
import { ErrorRefresco } from "../components/ErrorRefresco";
import { R_EMAILS, diaMesHora, nombreComercio } from "../logic/emails";

const TEXTO_CALENTAMIENTO: Record<string, string> = { active: "Calentando", paused: "En pausa", completed: "Terminado", error: "Con error" };
const TEXTO_ERROR_CALENTAMIENTO: Record<string, string> = { error_email_account: "la casilla tiene un problema de conexión (SMTP o IMAP): revisala en Snov" };

const selectCls = "h-9 max-w-[240px] rounded-lg border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50";

function OpcionesComercio({ comercios }: { comercios: ComercioCorto[] }) {
  return (
    <>
      {comercios.map((c) => <option key={c.brandId} value={`m:${c.brandId}`}>{c.marca || c.brandId}{c.marca && c.marca.toLowerCase() !== c.brandId ? ` (${c.brandId})` : ""}</option>)}
    </>
  );
}

/** «Guardar» y «Cancelar» de un cambio que todavía no se aplicó, con lo que va a pasar. */
function Confirmar({ children, guardando, onGuardar, onCancelar }: { children: ReactNode; guardando: boolean; onGuardar: () => void; onCancelar: () => void }) {
  return (
    <div className="mt-2 max-w-[320px] rounded-lg border border-violet-200 bg-violet-50 p-2.5 text-xs text-slate-700">
      {children}
      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={onGuardar} disabled={guardando}
          className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-2.5 py-1 font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
          {guardando && <Loader2 size={12} className="animate-spin" />} Guardar
        </button>
        <button type="button" onClick={onCancelar} disabled={guardando}
          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function FilaCasilla({ r, comercios, onCambio }: { r: RemitenteConexion; comercios: ComercioCorto[]; onCambio: () => void }) {
  const actual = r.brandId ? `m:${r.brandId}` : "";
  const [elegido, setElegido] = useState(actual);
  const [anteriores, setAnteriores] = useState<"quedan" | "pasan">("quedan");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [hecho, setHecho] = useState("");
  const [visto, setVisto] = useState(actual);
  const cal = r.calentamiento;
  // Lo que llega del servidor (se guardó, o lo cambió otro) manda. Se ajusta en el render y no en un useEffect: el de
  // montaje corría después de que se pintara la fila y pisaba una elección hecha en ese medio.
  if (visto !== actual) {
    setVisto(actual);
    setElegido(actual);
    setAnteriores("quedan");
  }
  const nuevo = elegido ? elegido.slice(2) : null;
  const nombreNuevo = nuevo ? nombreComercio(nuevo, comercios) : "";
  const nombreAntes = r.brandId ? nombreComercio(r.brandId, comercios) : "";
  const guardar = async () => {
    setGuardando(true);
    setError("");
    setHecho("");
    try {
      const l = leerRespuesta<ResultadoAsignacion>(await emailsApi.asignarRemitente(r.id, nuevo, anteriores));
      if (!l.ok) setError(textoError(l));
      else {
        const quedaron = l.data?.quedaron || 0;
        setHecho(quedaron ? `Listo. ${num(quedaron)} ${plural(quedaron, "campaña se quedó", "campañas se quedaron")} con ${nombreAntes}.` : "Listo.");
        onCambio();
      }
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };
  return (
    <tr className="align-top">
      <td className="px-4 py-3 min-w-[200px]">
        <p className="font-semibold text-slate-900">{r.nombre || r.email}</p>
        <p className="text-xs text-slate-500 break-all">{r.email}</p>
        {!r.enSnov && <p className="text-[11px] text-slate-400">Ya no está en Snov</p>}
      </td>
      <td className="px-4 py-3 text-xs">
        {r.suspendido ? <span className="font-semibold text-red-700">Suspendida</span> : r.valido ? <span className="text-emerald-700">Conectada</span> : <span className="font-semibold text-amber-700">Con problemas</span>}
        {r.imapValido === false && <p className="text-amber-700">El IMAP no anda: no se leen las respuestas</p>}
        <p className="text-slate-400">hasta {num(r.limiteDiario)} por día</p>
      </td>
      <td className="px-4 py-3 text-xs min-w-[180px]">
        {cal ? (
          <>
            <p className={cal.estado === "error" ? "font-semibold text-amber-700" : "text-slate-700"}>{TEXTO_CALENTAMIENTO[cal.estado] || cal.estado}{cal.estado === "error" && cal.error ? `: ${TEXTO_ERROR_CALENTAMIENTO[cal.error] || cal.error}` : ""}</p>
            <p className="text-slate-400">
              {cal.entregabilidad !== null ? `entregabilidad ${num(cal.entregabilidad)} %` : ""}{cal.rescatadosDeSpam ? ` · ${num(cal.rescatadosDeSpam)} rescatados de spam` : ""}
              {cal.porDia ? ` · hasta ${num(cal.porDia)} por día` : ""}
            </p>
          </>
        ) : <span className="text-slate-400">Sin calentamiento</span>}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600 text-right tabular-nums">{num(r.campanas)}</td>
      <td className="px-4 py-3">
        <label className="sr-only" htmlFor={`casilla-${r.id}`}>Comercio de {r.email}</label>
        <select id={`casilla-${r.id}`} value={elegido} disabled={guardando} onChange={(e) => { setElegido(e.target.value); setHecho(""); setError(""); }} className={selectCls}>
          <option value="">Sin comercio</option>
          {r.brandId && !comercios.some((c) => c.brandId === r.brandId) && <option value={`m:${r.brandId}`}>{r.brandId}</option>}
          <OpcionesComercio comercios={comercios} />
        </select>
        {elegido !== actual && (
          <Confirmar guardando={guardando} onGuardar={() => void guardar()} onCancelar={() => setElegido(actual)}>
            <p>{nuevo ? `Lo que mande desde ahora va a ser de ${nombreNuevo}: lo ve su gente, con los mails de las personas.` : "Lo que mande desde ahora no va a ser de ningún comercio."}</p>
            {r.brandId && r.campanas > 0 ? (
              <fieldset className="mt-1.5 space-y-1">
                <legend className="font-semibold">{r.campanas === 1 ? "La campaña que ya mandó:" : `Las ${num(r.campanas)} campañas que ya mandó:`}</legend>
                <label className="flex items-start gap-1.5">
                  <input type="radio" name={`anteriores-${r.id}`} checked={anteriores === "quedan"} onChange={() => setAnteriores("quedan")} className="mt-0.5" />
                  <span>Siguen siendo de {nombreAntes}</span>
                </label>
                <label className="flex items-start gap-1.5">
                  <input type="radio" name={`anteriores-${r.id}`} checked={anteriores === "pasan"} onChange={() => setAnteriores("pasan")} className="mt-0.5" />
                  <span>{nuevo ? `Pasan a ${nombreNuevo} (${nombreAntes} estaba mal)` : `Dejan de verse en ${nombreAntes} (estaba mal)`}</span>
                </label>
              </fieldset>
            ) : !r.brandId && r.campanas > 0 && nuevo ? (
              <p className="mt-1">{r.campanas === 1 ? "La campaña que ya mandó pasa" : `Las ${num(r.campanas)} campañas que ya mandó pasan`} a {nombreNuevo} (las que estén por la casilla).</p>
            ) : null}
          </Confirmar>
        )}
        {hecho && <p className="mt-1 text-xs text-emerald-700" role="status">{hecho}</p>}
        {error && <p className="mt-1 text-xs text-red-700" role="alert">{error}</p>}
      </td>
    </tr>
  );
}

function valorAsignacion(c: CampanaConexion): string {
  if (c.asignacion.modo === "manual" && c.asignacion.brandId) return `m:${c.asignacion.brandId}`;
  return c.asignacion.modo === "ninguno" ? "ninguno" : "auto";
}

/** Qué comercio sale por las casillas de una campaña, dicho como en el select («HATSU», «la casilla no tiene comercio»). */
function textoPorCasilla(remitentes: { id: number }[], casillas: Map<number, { brandId: string | null }>, comercios: ComercioCorto[]): string {
  if (!remitentes.length) return "sin casilla";
  const marcas = remitentes.map((r) => casillas.get(r.id)?.brandId || "");
  if (marcas.every((m) => !m)) return remitentes.length === 1 ? "la casilla no tiene comercio" : "las casillas no tienen comercio";
  if (marcas.some((m) => !m)) return "una de sus casillas no tiene comercio";
  if (new Set(marcas).size > 1) return "casillas de dos comercios";
  return nombreComercio(marcas[0], comercios);
}

function FilaCampana({ c, comercios, casillas, onCambio }: { c: CampanaConexion; comercios: ComercioCorto[]; casillas: Map<number, RemitenteConexion>; onCambio: () => void }) {
  const actual = valorAsignacion(c);
  const [elegido, setElegido] = useState(actual);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [visto, setVisto] = useState(actual);
  // Como en FilaCasilla: lo del servidor manda, ajustado en el render.
  if (visto !== actual) {
    setVisto(actual);
    setElegido(actual);
  }
  const porCasilla = textoPorCasilla(c.remitentes, casillas, comercios);
  const guardar = async () => {
    const modo: ModoAsignacion = elegido.startsWith("m:") ? "manual" : elegido === "ninguno" ? "ninguno" : "auto";
    setGuardando(true);
    setError("");
    try {
      const l = leerRespuesta(await emailsApi.asignarCampana(c.id, modo, modo === "manual" ? elegido.slice(2) : undefined));
      if (!l.ok) setError(textoError(l));
      else onCambio();
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };
  const efecto = elegido.startsWith("m:")
    ? `La va a ver ${nombreComercio(elegido.slice(2), comercios)}, con los mails de las personas y lo que contestaron.`
    : elegido === "ninguno"
      ? "No la va a ver ningún comercio."
      : `Vuelve a decidirse por la casilla (${porCasilla}).`;
  return (
    <tr className="align-top">
      <td className="px-4 py-3 min-w-[240px]">
        <div className="flex items-center gap-2 flex-wrap">
          {c.brandId ? (
            <span className="font-semibold text-slate-900">{c.titulo || `Campaña ${c.id}`}</span>
          ) : (
            <span className="font-semibold text-slate-700">{c.titulo || `Campaña ${c.id}`}</span>
          )}
          <ChipEstadoCampana estado={c.estado} />
        </div>
        <p className="text-xs text-slate-400 break-words">{c.remitentes.length ? `desde ${c.remitentes.map((r) => r.email || `casilla ${r.id}`).join(", ")}` : "sin casilla"}{c.iniciadaEn ? ` · salió ${diaMesHora(c.iniciadaEn)}` : ""}</p>
        {c.errorSync && <p className="mt-1 text-xs text-amber-700 inline-flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> {c.errorSync}</p>}
        {c.diferencias.length > 0 && (
          <p className="mt-1 text-[11px] text-slate-500">
            Distinto de la analítica de Snov: {c.diferencias.join(" · ")}. En una campaña que está enviando suele ser el atraso de Snov y se acomoda solo.
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-slate-700">{num(c.enviados)}</td>
      <td className="px-4 py-3 min-w-[230px]">
        <label className="sr-only" htmlFor={`campana-${c.id}`}>Comercio de {c.titulo}</label>
        <select id={`campana-${c.id}`} value={elegido} disabled={guardando} onChange={(e) => { setElegido(e.target.value); setError(""); }} className={selectCls}>
          <option value="auto">Por la casilla ({porCasilla})</option>
          <option value="ninguno">No es de un comercio</option>
          {c.asignacion.modo === "manual" && c.asignacion.brandId && !comercios.some((x) => x.brandId === c.asignacion.brandId) && (
            <option value={`m:${c.asignacion.brandId}`}>{c.asignacion.brandId}</option>
          )}
          <OpcionesComercio comercios={comercios} />
        </select>
        <p className="mt-1 text-xs">
          {c.brandId ? <span className="text-emerald-700">La ve {nombreComercio(c.brandId, comercios)}</span> : <span className="text-slate-500">No la ve ningún comercio</span>}
        </p>
        {elegido !== actual && (
          <Confirmar guardando={guardando} onGuardar={() => void guardar()} onCancelar={() => setElegido(actual)}>
            <p>{efecto}</p>
          </Confirmar>
        )}
        {c.sugerido && !c.brandId && c.asignacion.modo !== "ninguno" && elegido === actual && (
          <button type="button" disabled={guardando} onClick={() => setElegido(`m:${c.sugerido}`)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:underline disabled:opacity-50">
            <Sparkles size={12} /> Asignar a {nombreComercio(c.sugerido, comercios)} (por el nombre)
          </button>
        )}
        {error && <p className="mt-1 text-xs text-red-700" role="alert">{error}</p>}
      </td>
    </tr>
  );
}

export default function ConexionSnovPage() {
  const { estado, version, actualizando, actualizar, recargar: recargarEstado } = useEstadoEmails();
  const r = usePedido<Conexion>(() => emailsApi.conexion(), "conexion", { refresco: version });
  const [aviso, setAviso] = useState("");
  const [pidiendo, setPidiendo] = useState(false);
  const cambio = () => {
    r.recargar();
    recargarEstado();
  };
  const e = estado?.equipo;
  const res = e?.resultado;
  const casillas = new Map((r.dato?.remitentes || []).map((x) => [x.id, x]));

  const apretar = async () => {
    if (pidiendo || actualizando) return;
    setPidiendo(true);
    setAviso(await actualizar());
    setPidiendo(false);
  };

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">Conexión con Snov</h2>
        <p className="text-sm text-slate-500 mt-1">La cuenta de Snov de Deenex: sus casillas, y de qué comercio es cada campaña</p>
        {r.dato && r.error && <ErrorRefresco error={r.error} onReintentar={r.recargar} />}
      </header>

      <Tarjeta titulo="Actualización" sub={estado ? `Se trae sola cada ${num(estado.cadaMin)} minutos` : undefined}
        right={estado?.configurado ? (
          <button type="button" onClick={() => void apretar()} disabled={pidiendo || actualizando}
            className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
            {pidiendo || actualizando ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Actualizar ahora
          </button>
        ) : undefined}>
        {!estado ? <Esqueleto alto="h-16" /> : !estado.configurado ? (
          <p className="text-sm text-amber-800">Faltan SNOV_CLIENT_ID y SNOV_CLIENT_SECRET en el servidor. Se sacan de Snov → Integraciones → API.</p>
        ) : (
          <div className="space-y-2 text-sm text-slate-700">
            {actualizando ? (
              <p className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin text-violet-600" /> Actualizando{e?.total ? `: ${num(e.hechas)} de ${num(e.total)} campañas${e.paso ? ` (${e.paso})` : ""}` : "…"}</p>
            ) : e?.fin ? (
              <p className="inline-flex items-center gap-2">
                {e.ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-600" />}
                {e.ok ? "La última salió bien" : `La última falló: ${e.error}`} · {haceTexto(e.fin, new Date())} ({e.motivo === "manual" ? `a mano${e.pedidaPor ? `, ${e.pedidaPor}` : ""}` : "programada"})
              </p>
            ) : <p>Todavía no se actualizó nunca.</p>}
            {res && !actualizando && (
              <p className="text-xs text-slate-500">
                {[
                  `${num(res.campanas || 0)} ${plural(res.campanas || 0, "campaña", "campañas")} en Snov`,
                  `${num(res.revisadas || 0)} ${plural(res.revisadas || 0, "leída", "leídas")}`,
                  `${num(res.eventosNuevos || 0)} ${plural(res.eventosNuevos || 0, "evento nuevo", "eventos nuevos")}`,
                  `${num(res.respuestasNuevas || 0)} ${plural(res.respuestasNuevas || 0, "respuesta nueva", "respuestas nuevas")}`,
                  `${num(res.pedidos || 0)} ${plural(res.pedidos || 0, "pedido", "pedidos")} a Snov`,
                  ...(res.duracionMs ? [`${Math.round(res.duracionMs / 1000)} s`] : []),
                ].join(" · ")}
              </p>
            )}
            {!actualizando && (res?.errores || []).length > 0 && (
              <ul className="text-xs text-amber-800 space-y-0.5">
                {(res?.errores || []).map((x, i) => <li key={i}>{x.titulo ? `${x.titulo}: ` : ""}{x.error}</li>)}
              </ul>
            )}
            {aviso && <p className="text-xs text-amber-700" role="alert">{aviso}</p>}
          </div>
        )}
      </Tarjeta>

      {!r.dato ? (
        r.error ? <ErrorTarjeta texto={`No pudimos traer la conexión. ${r.error}`} onReintentar={r.recargar} /> : <Esqueleto alto="h-48" etiqueta="Cargando la conexión" />
      ) : (
        <>
          <Tarjeta titulo="Casillas" sub="Elegí de qué comercio es cada casilla: sus campañas quedan de ese comercio">
            {!r.dato.remitentes.length ? <Vacio>Snov no tiene casillas conectadas.</Vacio> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Casilla</th>
                      <th className="px-4 py-2">Estado</th>
                      <th className="px-4 py-2">Calentamiento</th>
                      <th className="px-4 py-2 text-right">Campañas</th>
                      <th className="px-4 py-2">Comercio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {r.dato.remitentes.map((x) => <FilaCasilla key={x.id} r={x} comercios={r.dato!.comercios} onCambio={cambio} />)}
                  </tbody>
                </table>
              </div>
            )}
          </Tarjeta>

          <Tarjeta titulo="Campañas" sub="Por la casilla, a mano, o «No es de un comercio» para una de Deenex o una prueba">
            {!r.dato.campanas.length ? <Vacio>Snov no tiene campañas.</Vacio> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Campaña</th>
                      <th className="px-4 py-2 text-right">Enviados</th>
                      <th className="px-4 py-2">Comercio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {r.dato.campanas.map((x) => <FilaCampana key={x.id} c={x} comercios={r.dato!.comercios} casillas={casillas} onCambio={cambio} />)}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-3 text-[11px] text-slate-400">
              Una campaña de un comercio la ve su gente en <Link to={R_EMAILS} className="underline">Emails</Link>, con los mails de las personas. En la duda no se asigna.
            </p>
          </Tarjeta>
        </>
      )}
    </div>
  );
}
